/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import React, { useMemo } from 'react';
import type { Coordinates, SoundingsCollection } from '@opengeoweb/shared';
import { referenceTimeUtils } from '@opengeoweb/shared';
import {
  getCollectionLocations,
  getEdrCollectionForLocation,
  getEdrCollectionForPosition,
  getEdrCollectionForRadius,
  getInstanceDetails,
  getInstanceLocations,
} from './api';
import { soundingsQueryOptions } from './queryOptions';
import type {
  CollectionDetails,
  SoundingFeature,
  SoundingsInstance,
  SoundingsInstances,
} from './types';
import {
  parseCoverageCollection,
  getTimestepsFromInstanceOrCollection,
  buildIntervalDatetime,
  extractGridPointsFromCoverage,
  getVerticalRangeFromInstance,
} from './utils';
import type { ServiceWithLocations } from '../../components/MenuBar/LocationsMenu';
import type { SoundingsCollectionsWithInstances } from '../../components/Soundings/types';
import type { SourceType } from '../../store/types';

export const sortSoundingFeatures = (
  data: SoundingFeature[],
): SoundingFeature[] => {
  return data.sort((featureA, featureB) => {
    // Sort by date (ascending)
    if (featureA.properties.time !== featureB.properties.time) {
      return featureA.properties.time > featureB.properties.time ? 1 : -1;
    }
    // Sort by pressure (descending)
    return featureB.properties.pressure - featureA.properties.pressure;
  });
};

const constructQueryParams = (
  collectionId: string,
  baseUrl: string,
  instanceId: string | undefined,
  location: string | Coordinates,
  datetime: string,
  parameterMappings: Record<string, string>,
  windSpeedUnit: string,
  verticalRange: string,
): { queryKey: string[]; queryFn: () => Promise<SoundingFeature[]> } => {
  const parameters = getParameterMappings(parameterMappings);
  if (typeof location === 'string') {
    // LOCATION call
    return {
      queryKey: [
        'edr-soundings-for-location',
        collectionId,
        instanceId || '',
        location,
        parameters.join(),
        datetime,
        verticalRange,
      ],
      queryFn: async (): Promise<SoundingFeature[]> => {
        // No collection, location or time selected, don't do EDR call but return empty array
        if (collectionId === undefined || location === '' || datetime === '') {
          return [];
        }

        try {
          const rawData = await getEdrCollectionForLocation(
            collectionId,
            baseUrl,
            instanceId,
            location,
            parameters,
            datetime,
            verticalRange,
          );
          const parsedData = parseCoverageCollection(
            rawData,
            parameterMappings,
            windSpeedUnit,
          );

          return sortSoundingFeatures(parsedData);
        } catch (_err) {
          throw new Error('Error fetching EDR data collection by location');
        }
      },
    };
  }
  // POSITION call
  return {
    queryKey: [
      'edr-soundings-for-position',
      collectionId,
      instanceId || '',
      location.join(),
      parameters.join(),
      datetime,
      verticalRange,
    ],
    queryFn: async (): Promise<SoundingFeature[]> => {
      // No collection, position or time selected, don't do EDR call but return empty array
      if (
        collectionId === undefined ||
        location === undefined ||
        datetime === ''
      ) {
        return [];
      }

      try {
        const rawData = await getEdrCollectionForPosition(
          collectionId,
          baseUrl,
          instanceId,
          location,
          parameters,
          datetime,
          verticalRange,
        );
        const parsedData = parseCoverageCollection(
          rawData,
          parameterMappings,
          windSpeedUnit,
        );

        return sortSoundingFeatures(parsedData);
      } catch (_err) {
        throw new Error('Error fetching EDR data collection by position');
      }
    },
  };
};

export const defaultParameterMappings = {
  temperature: 'temperature',
  windDirection: 'winddirection',
  windSpeed: 'windspeedms',
  windU: 'windums',
  windV: 'windvms',
  pressure: 'pressure',
  dewpoint: 'dewpoint',
  humidity: 'humidity',
  specificHumidity: 'specifichumidity',
  relativeHumidity: 'relativehumidity',
};

export const getParameterMappings = (
  parameterMappings: Record<string, string>,
): string[] => {
  return Object.keys(parameterMappings).map((key) => parameterMappings[key]);
};

export const useMultipleSources = (
  sources: SourceType[],
  locations: Record<string, string | undefined | Coordinates>,
  datetime?: string,
): {
  data: SoundingFeature[][];
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const {
    data: timesteps,
    error: timestepsError,
    isPending: timestepsPending,
  } = useTimesteps(sources);
  const queries = useMemo(() => {
    return sources.map(
      (
        {
          collectionId,
          url,
          instanceId,
          parameterMappings,
          windSpeedUnit,
          verticalRange,
          prefetchAllTimesteps,
        },
        index,
      ) => {
        const sourceTimesteps = timesteps[index] ?? [];
        const location = locations[collectionId] || '';
        const resolvedParamMappings =
          parameterMappings || defaultParameterMappings;
        const resolvedWindUnit = windSpeedUnit || 'ms';
        const resolvedVerticalRange = verticalRange || '';

        // Prefetch path: fetch the entire time range once, then select the right timestep
        if (prefetchAllTimesteps && sourceTimesteps.length > 0) {
          const intervalDatetime = buildIntervalDatetime(sourceTimesteps);

          let timeToShow = '';
          if (datetime) {
            const requestedTime = new Date(datetime).getTime();
            if (Number.isFinite(requestedTime)) {
              timeToShow =
                sourceTimesteps.find(
                  (t) => new Date(t).getTime() === requestedTime,
                ) ?? '';
            }
          }

          return {
            ...constructQueryParams(
              collectionId,
              url,
              instanceId,
              location,
              intervalDatetime,
              resolvedParamMappings,
              resolvedWindUnit,
              resolvedVerticalRange,
            ),
            select: (allFeatures: SoundingFeature[]): SoundingFeature[] => {
              if (!timeToShow || allFeatures.length === 0) {
                return [];
              }
              const timeToShowMs = new Date(timeToShow).getTime();
              return allFeatures.filter(
                (feat) =>
                  new Date(feat.properties.time).getTime() === timeToShowMs,
              );
            },
          };
        }

        // Per-timestep path: one fetch per selected time
        let dateToUse = '';
        if (datetime && sourceTimesteps.length > 0) {
          const requestedTime = new Date(datetime).getTime();
          if (Number.isFinite(requestedTime)) {
            const exactMatch = sourceTimesteps.find(
              (timestep) => new Date(timestep).getTime() === requestedTime,
            );
            dateToUse = exactMatch || '';
          }
        }

        return constructQueryParams(
          collectionId,
          url,
          instanceId,
          location,
          dateToUse,
          resolvedParamMappings,
          resolvedWindUnit,
          resolvedVerticalRange,
        );
      },
    );
  }, [datetime, locations, sources, timesteps]);

  return useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        return result.data || [];
      });
      const isPending =
        timestepsPending || queryResults.some((result) => result.isPending);
      const error =
        timestepsError || queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });
};

/**
 * Resolve relative reference-time tokens ("latest-N") on the given sources to
 * concrete instance ids against the freshest instance list, recomputing the
 * vertical range from the resolved instance. Sources with an absolute instance
 * id pass through unchanged. Re-resolving on every refetch is what makes a
 * "Latest" selection follow newly published model runs.
 */
export const useResolvedSourceSelections = (
  sources: SourceType[],
): SourceType[] => {
  const queries = useMemo(
    () =>
      sources.map(({ collectionId, url, instanceId }) => ({
        ...soundingsQueryOptions.getCollectionInstances(collectionId, url),
        enabled: referenceTimeUtils.isRelativeReferenceTime(instanceId),
        select: (data: SoundingsInstances): SoundingsInstance[] =>
          data.instances,
      })),
    [sources],
  );

  const instancesBySource = useQueries({
    queries,
    combine: (queryResults) => queryResults.map((result) => result.data ?? []),
  });

  const resolvedSourcesRef = React.useRef<SourceType[] | undefined>(undefined);
  return useMemo(() => {
    const resolvedSources = sources.map((source, index) => {
      const resolvedInstance = referenceTimeUtils.resolveReferenceTimeInstance(
        instancesBySource[index],
        source.instanceId,
      );
      if (!resolvedInstance) {
        return source;
      }
      return {
        ...source,
        instanceId: resolvedInstance.id,
        verticalRange: getVerticalRangeFromInstance(resolvedInstance),
      };
    });
    if (isEqual(resolvedSources, resolvedSourcesRef.current)) {
      return resolvedSourcesRef.current!;
    }
    resolvedSourcesRef.current = resolvedSources;
    return resolvedSources;
  }, [sources, instancesBySource]);
};

export const useTimesteps = (
  sourceSelections: SourceType[],
): { data: string[][]; error: Error | null; isPending: boolean } => {
  const queryClient = useQueryClient();
  const queries = React.useMemo(
    () =>
      sourceSelections.map(({ collectionId, instanceId, url }) => ({
        queryKey: ['edr-soundings-instance', collectionId, instanceId || ''],
        queryFn: async (): Promise<
          SoundingsInstance | SoundingsInstances | CollectionDetails
        > => {
          // If instance id passed, use this to fetch time extent
          if (instanceId && instanceId !== '') {
            const instanceData = await getInstanceDetails(
              collectionId,
              instanceId,
              url,
            );
            const timeExtentValues = instanceData?.extent?.temporal?.values;
            if (!timeExtentValues) {
              return queryClient.fetchQuery(
                soundingsQueryOptions.getCollectionInstances(collectionId, url),
              );
            }

            return instanceData;
          }
          // Otherwise, use collection details
          const collectionData = await queryClient.fetchQuery(
            soundingsQueryOptions.getCollectionDetails(collectionId, url),
          );

          return collectionData;
        },
        enabled: !referenceTimeUtils.isRelativeReferenceTime(instanceId),
        select: (
          data: SoundingsInstance | SoundingsInstances | CollectionDetails,
        ): string[] => {
          if ((data as SoundingsInstances).instances !== undefined) {
            const instance = (data as SoundingsInstances).instances.find(
              (instance) => instance.id === instanceId,
            );
            return getTimestepsFromInstanceOrCollection(instance);
          }
          return getTimestepsFromInstanceOrCollection(
            data as SoundingsInstance | CollectionDetails,
          );
        },
        refetchInterval: 5 * 60 * 1000,
      })),
    [sourceSelections, queryClient],
  );

  const results = useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => result.data || []);
      // The underlying query is generic, so we rewrap the error to be more specific
      const foundError = queryResults.find((result) => result?.error)?.error;
      const error = foundError
        ? new Error('Error fetching timesteps by collection', {
            cause: foundError,
          })
        : null;
      const isPending = queryResults.some((result) => result.isPending);

      return { data, error, isPending };
    },
  });

  return results;
};

export const useGetCollectionsInstances = (
  collections?: SoundingsCollection[],
  isDialogOpen?: boolean,
): {
  data: SoundingsCollectionsWithInstances[] | undefined;
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const collectionIds = useMemo(
    () =>
      collections?.map((collection) => {
        return { id: collection.id, url: collection.url };
      }) || [],
    [collections],
  );

  // Fetch collection details
  const queriesCollection = useMemo(
    () =>
      collectionIds.map(({ id, url }) => {
        return {
          ...soundingsQueryOptions.getCollectionDetails(id, url),
          enabled: isDialogOpen,
        };
      }),
    [collectionIds, isDialogOpen],
  );

  const resultsCollection = useQueries({
    queries: queriesCollection,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        return !!result.data?.data_queries?.instances;
      });
      // Only enabled queries should contribute to isPending to prevent a
      // loading indicator that never clears
      const isPending = queryResults.some((result, index) => {
        return !!queriesCollection[index]?.enabled && result.isPending;
      });
      const error = queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });

  // Fetch instance details for collections that have instances
  const queries = useMemo(() => {
    return collectionIds.map(({ id, url }, index) => {
      const hasInstances = !!resultsCollection.data[index];
      return {
        ...soundingsQueryOptions.getCollectionInstances(id, url),
        enabled: !!isDialogOpen && hasInstances,
      };
    });
  }, [collectionIds, isDialogOpen, resultsCollection.data]);

  const results = useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        const instances = [...(result.data?.instances ?? [])].sort(
          (featureA, featureB) => featureB.id.localeCompare(featureA.id),
        ); // Sort to get latest first
        return instances;
      });

      // Mirror the collection-details behavior: only enabled instance queries
      // participate in the loading state.
      const isPending = queryResults.some((result, index) => {
        return !!queries[index]?.enabled && result.isPending;
      });
      const error = queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });

  const dataWithCollectionInfo = collections?.map((collection, index) => {
    return {
      ...collection,
      instances: results.data[index] || [],
    };
  });

  const errorResult = results.error || resultsCollection.error;
  const isPendingResult = results.isPending || resultsCollection.isPending;

  return {
    ...results,
    isPending: isPendingResult,
    error: errorResult,
    data: dataWithCollectionInfo,
  };
};

export const useGetLocations = <
  SoundingsCollectionLike extends {
    id: string;
    url: string;
    name: string;
    instanceId?: string;
  },
>(
  collections: SoundingsCollectionLike[],
  enabled?: boolean,
): {
  data: ServiceWithLocations[] | undefined;
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const queryClient = useQueryClient();
  const edrQueries = useMemo(
    () =>
      collections?.map((collection) => ({
        queryKey: [
          'edr-locations',
          collection.id,
          collection.url,
          collection.instanceId ?? '',
        ],
        queryFn: async (): Promise<ServiceWithLocations> => {
          let resolvedInstanceId = collection.instanceId;
          if (
            resolvedInstanceId &&
            referenceTimeUtils.isRelativeReferenceTime(resolvedInstanceId)
          ) {
            try {
              const instances = await queryClient.fetchQuery(
                soundingsQueryOptions.getCollectionInstances(
                  collection.id,
                  collection.url,
                ),
              );
              const resolved = referenceTimeUtils.resolveReferenceTimeInstance(
                instances.instances,
                resolvedInstanceId,
              );
              // Skip instance-level only when data_queries is present but explicitly excludes locations — if data_queries is absent, try instance-level anyway
              const locationsExplicitlyUnsupported =
                resolved?.data_queries !== undefined &&
                !resolved.data_queries.locations;
              resolvedInstanceId = locationsExplicitlyUnsupported
                ? undefined
                : resolved?.id;
            } catch {
              resolvedInstanceId = undefined;
            }
          }
          if (resolvedInstanceId) {
            try {
              const locations = await getInstanceLocations(
                collection.id,
                resolvedInstanceId,
                collection.url,
              );
              return { ...locations, id: collection.id, name: collection.name };
            } catch {
              // fall back to collection-level if instance endpoint is not supported
            }
          }
          const locations = await getCollectionLocations(
            collection.id,
            collection.url,
          );
          return { ...locations, id: collection.id, name: collection.name };
        },
        enabled,
      })),
    [collections, enabled, queryClient],
  );

  return useQueries({
    queries: edrQueries,
    combine: (results) => {
      const data = results
        .map((result) => result.data)
        .filter((result) => !!result) as ServiceWithLocations[];
      const isPending = results.some((result) => result.isPending);
      const error = results.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });
};

/**
 * Hook to check whether EDR position queries are supported for each source.
 * Returns a record mapping collectionId to boolean indicating position support.
 *
 * This is used to determine whether to use position queries (exact coordinates)
 * or fall back to location queries (nearest station marker).
 */
export const useCheckPositionQuerySupportBySource = (
  sources: SourceType[],
): { data: Record<string, boolean>; isPending: boolean } => {
  // Ref to stabilize data object reference when values haven't changed
  const dataRef = React.useRef<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const queries = useMemo(
    () =>
      sources.map(({ collectionId, url, instanceId }) => ({
        queryKey: ['edr-position-support', collectionId, instanceId || ''],
        queryFn: async (): Promise<{
          collectionId: string;
          supported: boolean;
        }> => {
          try {
            if (instanceId) {
              const instances = await queryClient.fetchQuery(
                soundingsQueryOptions.getCollectionInstances(collectionId, url),
              );
              const instance = instances.instances.find(
                (i) => i.id === instanceId,
              );
              return {
                collectionId,
                supported: !!instance?.data_queries?.position,
              };
            }
            const details = await queryClient.fetchQuery(
              soundingsQueryOptions.getCollectionDetails(collectionId, url),
            );
            return {
              collectionId,
              supported: !!details?.data_queries?.position,
            };
          } catch {
            // If we can't fetch details, assume position is not supported
            return { collectionId, supported: false };
          }
        },
        enabled: !referenceTimeUtils.isRelativeReferenceTime(instanceId),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        // Preserve previous result while a new instance check is in-flight to avoid spurious location updates.
        placeholderData: (
          prev: { collectionId: string; supported: boolean } | undefined,
        ): { collectionId: string; supported: boolean } | undefined => prev,
      })),
    [sources, queryClient],
  );

  return useQueries({
    queries,
    combine: (queryResults) => {
      const newData = queryResults.reduce<Record<string, boolean>>(
        (acc, result) => {
          if (result.data) {
            return {
              ...acc,
              [result.data.collectionId]: result.data.supported,
            };
          }
          return acc;
        },
        {},
      );
      // Stabilize data reference to prevent unnecessary effect re-runs
      const data = isEqual(newData, dataRef.current)
        ? dataRef.current
        : newData;
      dataRef.current = data;
      const isPending = queryResults.some((result) => result.isPending);
      return { data, isPending };
    },
  });
};

/**
 * Hook to check whether EDR radius queries are supported for each source.
 * Returns a record mapping collectionId to boolean indicating radius support.
 *
 * This is used to determine whether we can fetch grid points within a radius
 * around the clicked position to show source-specific grid point markers.
 */
export const useCheckRadiusQuerySupportBySource = (
  sources: SourceType[],
): { data: Record<string, boolean>; isPending: boolean } => {
  // Ref to stabilize data object reference when values haven't changed
  const dataRef = React.useRef<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const queries = useMemo(
    () =>
      sources.map(({ collectionId, url, instanceId }) => ({
        queryKey: ['edr-radius-support', collectionId, instanceId || ''],
        queryFn: async (): Promise<{
          collectionId: string;
          supported: boolean;
        }> => {
          try {
            if (instanceId) {
              const instances = await queryClient.fetchQuery(
                soundingsQueryOptions.getCollectionInstances(collectionId, url),
              );
              const instance = instances.instances.find(
                (i) => i.id === instanceId,
              );
              return {
                collectionId,
                supported: !!instance?.data_queries?.radius,
              };
            }
            const details = await queryClient.fetchQuery(
              soundingsQueryOptions.getCollectionDetails(collectionId, url),
            );
            return {
              collectionId,
              supported: !!details?.data_queries?.radius,
            };
          } catch {
            // If we can't fetch details, assume radius is not supported
            return { collectionId, supported: false };
          }
        },
        enabled: !referenceTimeUtils.isRelativeReferenceTime(instanceId),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        placeholderData: (
          prev: { collectionId: string; supported: boolean } | undefined,
        ): { collectionId: string; supported: boolean } | undefined => prev,
      })),
    [sources, queryClient],
  );

  return useQueries({
    queries,
    combine: (queryResults) => {
      const newData = queryResults.reduce<Record<string, boolean>>(
        (acc, result) => {
          if (result.data) {
            return {
              ...acc,
              [result.data.collectionId]: result.data.supported,
            };
          }
          return acc;
        },
        {},
      );
      // Stabilize data reference to prevent unnecessary effect re-runs
      const data = isEqual(newData, dataRef.current)
        ? dataRef.current
        : newData;
      dataRef.current = data;
      const isPending = queryResults.some((result) => result.isPending);
      return { data, isPending };
    },
  });
};

/** Default grid resolution in km (used when source doesn't specify) */
const DEFAULT_GRID_RESOLUTION_KM = 2.5;

/**
 * Grid point data returned by useGridPointsFromRadius hook.
 * Contains the nearest grid point coordinates for each source.
 */
export interface GridPointData {
  sourceId: string;
  gridPoint: [number, number] | undefined;
  allGridPoints: [number, number][];
}

/**
 * Hook to fetch grid points within a radius around a center position for each source.
 * Uses EDR radius queries to find actual grid point positions.
 * Returns the nearest grid point to the center for each source.
 *
 * @param sources - Array of source configurations
 * @param radiusSupport - Record mapping collectionId to boolean indicating radius support
 * @param centerCoordinates - The clicked position [lon, lat]
 * @param datetime - Selected datetime for the query
 * @param enabled - Whether to run the queries
 */
export const useGridPointsFromRadius = (
  sources: SourceType[],
  radiusSupport: Record<string, boolean>,
  centerCoordinates: [number, number] | undefined,
  datetime: string | undefined,
  enabled = true,
): {
  data: Record<string, GridPointData>;
  isPending: boolean;
  error: Error | null;
} => {
  // Ref to stabilize data object reference when values haven't changed
  const dataRef = React.useRef<Record<string, GridPointData>>({});

  const queries = useMemo(() => {
    if (!centerCoordinates || !datetime || !enabled) {
      return sources.map(({ collectionId }) => ({
        queryKey: ['edr-grid-points-disabled', collectionId],
        queryFn: async (): Promise<GridPointData> => ({
          sourceId: collectionId,
          gridPoint: undefined,
          allGridPoints: [],
        }),
        enabled: false,
      }));
    }

    return sources.map(
      ({
        collectionId,
        url,
        instanceId,
        parameterMappings,
        verticalRange,
        gridResolution,
      }) => {
        const supportsRadius = radiusSupport[collectionId] ?? false;
        // Use 1.5x grid resolution to ensure we capture surrounding grid points.
        // Smaller values may fail to return results on some EDR servers.
        const radiusKm = (gridResolution ?? DEFAULT_GRID_RESOLUTION_KM) * 1.5;
        const resolvedParamMappings =
          parameterMappings || defaultParameterMappings;
        const parameters = getParameterMappings(resolvedParamMappings);

        return {
          queryKey: [
            'edr-grid-points-radius',
            collectionId,
            instanceId || '',
            centerCoordinates.join(','),
            radiusKm.toString(),
            datetime,
          ],
          queryFn: async (): Promise<GridPointData> => {
            if (!supportsRadius) {
              return {
                sourceId: collectionId,
                gridPoint: undefined,
                allGridPoints: [],
              };
            }

            try {
              // Optimization: Only request a single parameter to minimize data transfer
              // We only need the grid point coordinates, not the actual sounding data
              const minimalParameters = parameters.slice(0, 1); // Just one parameter
              // Use empty vertical range - let server return any level
              // (Don't hardcode '1000' as that only works for pressure-level sources,
              // not for hybrid/model-level sources which use levels 1-65)
              const minimalVerticalRange = '';

              const rawData = await getEdrCollectionForRadius(
                collectionId,
                url,
                instanceId,
                centerCoordinates,
                radiusKm,
                minimalParameters,
                datetime,
                minimalVerticalRange,
              );

              const allGridPoints = extractGridPointsFromCoverage(rawData);

              // Find the nearest grid point to the center coordinates
              let nearestPoint: [number, number] | undefined;
              let minSquaredDistance = Infinity;

              allGridPoints.forEach((point) => {
                const toRad = Math.PI / 180;
                const cosLat = Math.cos(toRad * centerCoordinates[1]);
                const dLon = (point[0] - centerCoordinates[0]) * cosLat;
                const dLat = point[1] - centerCoordinates[1];
                // No need to use square root here, we just want to compare distances, so we can compare squared distances
                const squaredDistance = dLon * dLon + dLat * dLat;

                if (squaredDistance < minSquaredDistance) {
                  minSquaredDistance = squaredDistance;
                  nearestPoint = point;
                }
              });

              return {
                sourceId: collectionId,
                gridPoint: nearestPoint,
                allGridPoints,
              };
            } catch {
              return {
                sourceId: collectionId,
                gridPoint: undefined,
                allGridPoints: [],
              };
            }
          },
          enabled: supportsRadius && enabled,
          staleTime: 30 * 1000, // Cache for 30 seconds
        };
      },
    );
  }, [sources, radiusSupport, centerCoordinates, datetime, enabled]);

  return useQueries({
    queries,
    combine: (queryResults) => {
      const newData = queryResults.reduce<Record<string, GridPointData>>(
        (acc, result) => {
          if (result.data) {
            return {
              ...acc,
              [result.data.sourceId]: result.data,
            };
          }
          return acc;
        },
        {},
      );

      // Stabilize data reference to prevent unnecessary re-renders
      const data = isEqual(newData, dataRef.current)
        ? dataRef.current
        : newData;
      dataRef.current = data;

      const isPending = queryResults.some((result) => result.isPending);
      const error = queryResults.find((result) => result?.error)?.error || null;

      return { data, isPending, error };
    },
  });
};
